import { useState, useEffect, useCallback } from 'react';
import { Shield, Plus, Trash2, AlertTriangle, Globe, Eye, Activity, Clock, MousePointerClick, Target, Download } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const SecurityTab = ({ getAuthHeader }) => {
  const [blockedIps, setBlockedIps] = useState([]);
  const [suspiciousIps, setSuspiciousIps] = useState([]);
  const [adClicks, setAdClicks] = useState([]);
  const [adClicksDays, setAdClicksDays] = useState(7);
  const [myIp, setMyIp] = useState(null);
  const [newIp, setNewIp] = useState('');
  const [newReason, setNewReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [showAllSuspicious, setShowAllSuspicious] = useState(false);
  const [showAllAdClicks, setShowAllAdClicks] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [b, me, sus, ads] = await Promise.all([
        axios.get(`${API}/admin/blocked-ips`, { headers: getAuthHeader() }),
        axios.get(`${API}/admin/blocked-ips/check-my-ip`, { headers: getAuthHeader() }).catch(() => ({ data: null })),
        axios.get(`${API}/admin/suspicious-ips?days=7&min_bookings=2`, { headers: getAuthHeader() }).catch(() => ({ data: { results: [] } })),
        axios.get(`${API}/admin/ad-clicks?days=${adClicksDays}`, { headers: getAuthHeader() }).catch(() => ({ data: { results: [] } })),
      ]);
      setBlockedIps(b.data);
      setMyIp(me.data?.ip);
      setSuspiciousIps(sus.data?.results || []);
      setAdClicks(ads.data?.results || []);
    } catch (e) { toast.error('Erreur chargement'); }
  }, [getAuthHeader, adClicksDays]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const addIp = async (ipParam = null, reasonParam = null) => {
    const ip = (ipParam || newIp).trim();
    const reason = reasonParam !== null ? reasonParam : newReason;
    if (!ip) return;
    if (myIp && (ip === myIp)) {
      if (!window.confirm(`⚠ ATTENTION : ${ip} est VOTRE adresse IP actuelle. Vous serez bloqué et devrez utiliser un autre réseau pour vous débloquer. Continuer ?`)) return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/admin/blocked-ips`, { ip, reason }, { headers: getAuthHeader() });
      toast.success(`${ip} bloquée`);
      if (!ipParam) { setNewIp(''); setNewReason(''); }
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    } finally { setLoading(false); }
  };

  const removeIp = async (id, ip) => {
    if (!window.confirm(`Débloquer ${ip} ?`)) return;
    try {
      await axios.delete(`${API}/admin/blocked-ips/${id}`, { headers: getAuthHeader() });
      toast.success(`${ip} débloquée`);
      fetchData();
    } catch (e) { toast.error('Erreur'); }
  };

  const exportGoogleAdsCsv = async () => {
    try {
      const r = await axios.get(`${API}/admin/ad-clicks/export-google-ads?days=${adClicksDays}&min_score=40`, {
        headers: getAuthHeader(),
        responseType: 'blob',
      });
      const exportedCount = r.headers['x-exported-count'] || '?';
      const url = URL.createObjectURL(new Blob([r.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `google-ads-exclusions-${new Date().toISOString().slice(0,10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`${exportedCount} IPs exportées pour Google Ads`);
    } catch (e) { toast.error('Erreur export CSV'); }
  };

  const riskColor = (score) => {
    if (score >= 70) return 'bg-red-500/20 border-red-500/40 text-red-300';
    if (score >= 40) return 'bg-amber-500/20 border-amber-500/40 text-amber-300';
    return 'bg-zinc-700/40 border-zinc-600 text-zinc-300';
  };

  const visibleSuspicious = showAllSuspicious ? suspiciousIps : suspiciousIps.slice(0, 10);
  const visibleAdClicks = showAllAdClicks ? adClicks : adClicks.slice(0, 15);
  const flagEmoji = (cc) => {
    if (!cc || cc.length !== 2) return '🌐';
    const A = 0x1F1E6, base = 'A'.charCodeAt(0);
    return String.fromCodePoint(A + cc.charCodeAt(0) - base) + String.fromCodePoint(A + cc.charCodeAt(1) - base);
  };

  return (
    <div className="space-y-6 animate-fade-in-up">
      {/* Google Ads click tracking */}
      <div className="glass-card rounded-xl p-6">
        <div className="flex items-start justify-between mb-4 flex-wrap gap-3">
          <div>
            <h3 className="text-lg font-bold text-blue-300 flex items-center gap-2">
              <Target className="w-5 h-5" /> Trafic Google Ads — Détection click fraud
            </h3>
            <p className="text-zinc-400 text-xs mt-1 max-w-2xl">
              Liste des IPs ayant cliqué sur vos annonces Google (paramètre <code className="text-emerald-400">?gclid=</code>) ces derniers jours.
              Score 60+ = clics multiples sans réservation (probable concurrent).
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-zinc-500 text-xs">Période :</span>
            <select
              value={adClicksDays}
              onChange={e => setAdClicksDays(parseInt(e.target.value))}
              className="px-2 py-1 bg-zinc-800 border border-zinc-700 rounded text-white text-xs"
              data-testid="ad-clicks-period"
            >
              <option value={1}>24 heures</option>
              <option value={7}>7 jours</option>
              <option value={30}>30 jours</option>
            </select>
            <Button
              size="sm"
              onClick={exportGoogleAdsCsv}
              disabled={adClicks.length === 0}
              className="bg-blue-600 hover:bg-blue-700 text-white text-xs h-7 px-2"
              data-testid="export-google-ads-btn"
              title="Télécharger un CSV des IPs suspectes (score 40+) au format directement importable dans Google Ads → Outils → Exclusions IP"
            >
              <Download className="w-3 h-3 mr-1" /> Export Google Ads
            </Button>
          </div>
        </div>

        {adClicks.length === 0 ? (
          <p className="text-zinc-500 text-sm italic py-4">
            <MousePointerClick className="w-4 h-4 inline mr-1" />
            Aucun clic Google Ads détecté sur cette période. Le tracker se déclenche dès qu'un visiteur arrive avec <code className="text-emerald-400 mx-1">?gclid=</code> dans l'URL.
          </p>
        ) : (
          <>
            <div className="overflow-x-auto" data-testid="ad-clicks-table">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-wider text-zinc-500 border-b border-zinc-800">
                    <th className="py-2 pr-3">Risque</th>
                    <th className="py-2 pr-3">IP</th>
                    <th className="py-2 pr-3">Localisation</th>
                    <th className="py-2 pr-3">FAI</th>
                    <th className="py-2 pr-3">Clics</th>
                    <th className="py-2 pr-3">Réservations</th>
                    <th className="py-2 pr-3">Dernier clic</th>
                    <th className="py-2 pr-3">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleAdClicks.map(a => (
                    <tr key={a.ip} className="border-b border-zinc-800/50 hover:bg-zinc-800/30 transition" data-testid={`ad-click-row-${a.ip}`}>
                      <td className="py-2 pr-3">
                        <span className={`inline-block px-2 py-0.5 rounded font-bold text-xs border ${riskColor(a.fraud_score)}`}>
                          {a.fraud_score}
                        </span>
                      </td>
                      <td className="py-2 pr-3">
                        <code className="text-emerald-300 font-mono text-xs">{a.ip}</code>
                        {a.is_blocked && (
                          <span className="ml-1 inline-block px-1.5 py-0.5 bg-red-500/20 border border-red-500/40 rounded text-red-300 text-[9px]">BLOQUÉE</span>
                        )}
                      </td>
                      <td className="py-2 pr-3 text-zinc-300 text-xs">
                        <span className="mr-1">{flagEmoji(a.country_code)}</span>
                        {a.city ? `${a.city}, ` : ''}{a.country || '—'}
                      </td>
                      <td className="py-2 pr-3 text-zinc-500 text-[10px] max-w-[160px] truncate" title={a.isp}>{a.isp || '—'}</td>
                      <td className="py-2 pr-3 text-white font-semibold">{a.clicks}</td>
                      <td className="py-2 pr-3 text-xs">
                        {a.bookings_total > 0 ? (
                          <span className="text-emerald-400">
                            {a.bookings_completed}✓ / {a.bookings_total}
                          </span>
                        ) : (
                          <span className="text-red-400">0</span>
                        )}
                      </td>
                      <td className="py-2 pr-3 text-zinc-400 text-xs whitespace-nowrap">
                        <Clock className="w-3 h-3 inline mr-1" />
                        {a.last_seen ? new Date(a.last_seen).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '-'}
                      </td>
                      <td className="py-2 pr-3">
                        {a.is_blocked ? (
                          <span className="text-zinc-500 text-[10px] italic">Bloquée</span>
                        ) : (
                          <Button
                            size="sm"
                            onClick={() => addIp(a.ip, `Click fraud : ${a.clicks} clics Ads, ${a.bookings_total} résa, ${a.country}`)}
                            className="bg-red-600 hover:bg-red-700 text-white text-xs h-7 px-2"
                            data-testid={`block-ad-click-${a.ip}`}
                          >
                            <Shield className="w-3 h-3 mr-1" /> Bloquer
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex items-center justify-between mt-3 text-xs text-zinc-500">
              <span>
                {adClicks.length} IP{adClicks.length > 1 ? 's' : ''} unique{adClicks.length > 1 ? 's' : ''} ayant cliqué sur vos annonces
              </span>
              {adClicks.length > 15 && (
                <button onClick={() => setShowAllAdClicks(!showAllAdClicks)} className="text-emerald-400 hover:underline flex items-center gap-1">
                  <Eye className="w-3 h-3" /> {showAllAdClicks ? 'Voir moins' : `Voir les ${adClicks.length - 15} autres`}
                </button>
              )}
            </div>
          </>
        )}
      </div>

      {/* Suspicious IPs table */}
      <div className="glass-card rounded-xl p-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="text-lg font-bold text-amber-400 flex items-center gap-2">
              <Activity className="w-5 h-5" /> IPs suspectes (7 derniers jours)
            </h3>
            <p className="text-zinc-400 text-xs mt-1 max-w-xl">
              Détection automatique basée sur le nombre de réservations, taux d'annulation, et numéros différents par IP. Score 70+ = haute priorité.
            </p>
          </div>
        </div>

        {suspiciousIps.length === 0 ? (
          <p className="text-zinc-500 text-sm italic py-4">Aucune IP suspecte détectée. 🎉</p>
        ) : (
          <>
            <div className="overflow-x-auto" data-testid="suspicious-ips-table">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-wider text-zinc-500 border-b border-zinc-800">
                    <th className="py-2 pr-3">Risque</th>
                    <th className="py-2 pr-3">IP</th>
                    <th className="py-2 pr-3">Réservations</th>
                    <th className="py-2 pr-3">Téléphones</th>
                    <th className="py-2 pr-3">Statuts</th>
                    <th className="py-2 pr-3">Dernière activité</th>
                    <th className="py-2 pr-3">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleSuspicious.map(s => (
                    <tr key={s.ip} className="border-b border-zinc-800/50 hover:bg-zinc-800/30 transition" data-testid={`suspicious-row-${s.ip}`}>
                      <td className="py-2 pr-3">
                        <span className={`inline-block px-2 py-0.5 rounded font-bold text-xs border ${riskColor(s.risk_score)}`} title="Score 0-100">
                          {s.risk_score}
                        </span>
                      </td>
                      <td className="py-2 pr-3">
                        <code className="text-emerald-300 font-mono text-xs">{s.ip}</code>
                        {s.is_blocked && (
                          <span className="ml-1 inline-block px-1.5 py-0.5 bg-red-500/20 border border-red-500/40 rounded text-red-300 text-[9px]">BLOQUÉE</span>
                        )}
                      </td>
                      <td className="py-2 pr-3 text-white font-semibold">{s.count}</td>
                      <td className="py-2 pr-3" title={(s.phones_sample || []).join(', ')}>
                        <span className={`font-semibold ${s.unique_phones >= 3 ? 'text-red-300' : 'text-zinc-300'}`}>
                          {s.unique_phones}
                        </span>
                        {s.unique_phones >= 3 && <span className="ml-1 text-[10px] text-red-400">⚠</span>}
                      </td>
                      <td className="py-2 pr-3 text-xs">
                        <span className="text-emerald-400">{s.completed}✓</span>
                        <span className="mx-1 text-zinc-600">·</span>
                        <span className={s.cancelled > 0 ? 'text-red-400' : 'text-zinc-500'}>{s.cancelled}✗</span>
                        <span className="mx-1 text-zinc-600">·</span>
                        <span className="text-amber-400">{s.pending}…</span>
                      </td>
                      <td className="py-2 pr-3 text-zinc-400 text-xs">
                        <Clock className="w-3 h-3 inline mr-1" />
                        {s.last_seen ? new Date(s.last_seen).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '-'}
                      </td>
                      <td className="py-2 pr-3">
                        {s.is_blocked ? (
                          <span className="text-zinc-500 text-[10px] italic">Déjà bloquée</span>
                        ) : (
                          <Button
                            size="sm"
                            onClick={() => addIp(s.ip, `Auto-détectée : ${s.count} résa, ${s.unique_phones} tels, score ${s.risk_score}`)}
                            className="bg-red-600 hover:bg-red-700 text-white text-xs h-7 px-2"
                            data-testid={`block-suspicious-${s.ip}`}
                          >
                            <Shield className="w-3 h-3 mr-1" /> Bloquer
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {suspiciousIps.length > 10 && (
              <button
                onClick={() => setShowAllSuspicious(!showAllSuspicious)}
                className="mt-3 text-xs text-emerald-400 hover:underline flex items-center gap-1"
              >
                <Eye className="w-3 h-3" /> {showAllSuspicious ? 'Voir moins' : `Voir les ${suspiciousIps.length - 10} autres IPs`}
              </button>
            )}
          </>
        )}
      </div>

      {/* Manual blocking */}
      <div className="glass-card rounded-xl p-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="text-lg font-bold text-red-400 flex items-center gap-2">
              <Shield className="w-5 h-5" /> Blocage IP — Anti-fraude
            </h3>
            <p className="text-zinc-400 text-xs mt-1 max-w-xl">
              Bloquez les IPs ou plages CIDR pour empêcher l'accès au site et aux endpoints de réservation.
              Les IPs bloquées reçoivent un HTTP 403 sur toutes les routes (sauf endpoints admin).
            </p>
          </div>
          {myIp && (
            <div className="text-right shrink-0" data-testid="security-my-ip">
              <p className="text-[10px] text-zinc-500 uppercase tracking-wider">Votre IP actuelle</p>
              <p className="text-emerald-300 font-mono text-sm">{myIp}</p>
            </div>
          )}
        </div>

        {/* Add IP form */}
        <div className="p-4 bg-zinc-900/70 rounded-lg border border-red-500/20 space-y-3 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <label className="text-zinc-400 text-xs block mb-1">Adresse IP ou CIDR *</label>
              <Input
                value={newIp}
                onChange={e => setNewIp(e.target.value)}
                placeholder="1.2.3.4 ou 192.168.0.0/24"
                className="bg-zinc-800 border-zinc-700 text-white text-sm font-mono"
                data-testid="security-new-ip"
              />
            </div>
            <div className="md:col-span-2">
              <label className="text-zinc-400 text-xs block mb-1">Raison (optionnel)</label>
              <Input
                value={newReason}
                onChange={e => setNewReason(e.target.value)}
                placeholder="Ex: Fausses réservations, clics Google Ads"
                className="bg-zinc-800 border-zinc-700 text-white text-sm"
                data-testid="security-new-reason"
              />
            </div>
          </div>
          <Button
            onClick={() => addIp()}
            disabled={!newIp.trim() || loading}
            className="bg-red-600 hover:bg-red-700 text-white text-sm"
            data-testid="security-add-btn"
          >
            <Plus className="w-4 h-4 mr-1" /> Bloquer cette IP
          </Button>
        </div>

        {/* Blocked IPs list */}
        <div className="space-y-2" data-testid="security-blocked-list">
          {blockedIps.length === 0 ? (
            <p className="text-zinc-500 text-sm italic">Aucune IP bloquée actuellement.</p>
          ) : (
            <>
              <p className="text-zinc-400 text-xs uppercase tracking-wider mb-2">
                {blockedIps.length} IP{blockedIps.length > 1 ? 's' : ''} bloquée{blockedIps.length > 1 ? 's' : ''}
              </p>
              {blockedIps.map(b => (
                <div key={b.id} className="flex items-center justify-between gap-3 p-3 bg-zinc-800/60 rounded-lg border border-zinc-700 hover:border-red-500/40 transition" data-testid={`security-row-${b.id}`}>
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    <div className="w-8 h-8 bg-red-500/10 rounded-full flex items-center justify-center shrink-0">
                      {b.is_cidr ? <Globe className="w-4 h-4 text-red-400" /> : <AlertTriangle className="w-4 h-4 text-red-400" />}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-white font-mono text-sm">
                        {b.ip}
                        {b.is_cidr && <span className="ml-2 inline-block px-2 py-0.5 bg-amber-500/10 border border-amber-500/30 rounded text-amber-300 text-[10px]">CIDR</span>}
                      </p>
                      <p className="text-zinc-500 text-xs">
                        {b.reason || 'Aucune raison'} • <span className="text-zinc-600">{new Date(b.created_at).toLocaleDateString('fr-FR')}</span>
                      </p>
                    </div>
                  </div>
                  <Button
                    size="sm" variant="outline"
                    onClick={() => removeIp(b.id, b.ip)}
                    className="border-zinc-700 text-zinc-300 hover:bg-emerald-500/10 hover:border-emerald-500/40 hover:text-emerald-300 text-xs h-8 shrink-0"
                    data-testid={`security-remove-${b.id}`}
                  >
                    <Trash2 className="w-3 h-3 mr-1" /> Débloquer
                  </Button>
                </div>
              ))}
            </>
          )}
        </div>
      </div>
    </div>
  );
};
